import React, { useState, useEffect } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DollarSign,
  CreditCard,
  Calendar,
  CheckCircle,
  ExternalLink,
  Settings,
  Zap,
  Star,
  ArrowRight,
  Lock,
  Rss,
  Copy,
  RefreshCw,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { useIcalFeed } from "@/hooks/useIcalFeed";
import { usePayoutStatus } from "@/hooks/usePayoutStatus";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

const BusinessSettingsPage = () => {
  const navigate = useNavigate();
  const { subscription, isLoading: subscriptionLoading, isUpgrading, upgradeError, upgradeToPro, getStripePortalUrl } = useSubscription();
  const { integration, isLoading: calendarLoading, isConnecting, connectGoogleCalendar, disconnectGoogleCalendar, refetch: refetchCalendar } = useGoogleCalendar();
  const { feedUrl, loading: icsLoading, rotate: rotateIcal } = useIcalFeed();
  const { status: payoutStatus, chargesEnabled, payoutsEnabled, accountId: payoutAccountId, isLoading: isLoadingPayout, refetch: refetchPayout } = usePayoutStatus();
  const [isConnectingStripe, setIsConnectingStripe] = useState(false);
  const [isOpeningPayoutAccount, setIsOpeningPayoutAccount] = useState(false);

  // Handle URL parameters for callbacks
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('subscription_success') === 'true') {
      toast.success("Successfully upgraded to Pro plan!");

      if (typeof window !== 'undefined' && (window as any).posthog) {
        (window as any).posthog.capture('upgrade_completed', { new_plan: 'pro' });
      }
    } else if (urlParams.get('subscription_canceled') === 'true') {
      toast.error("Subscription upgrade was canceled");
    }

    if (urlParams.get('stripe_success') === 'true') {
      toast.info("Payout setup submitted — verifying account status...");
      setTimeout(() => refetchPayout(), 1000);
    } else if (urlParams.get('stripe_refresh') === 'true') {
      toast.info("Please complete your payout setup");
    }

    const authCode = urlParams.get('code');
    if (authCode) {
      handleGoogleAuthCallback(authCode);
    }

    if (urlParams.has('subscription_success') || urlParams.has('subscription_canceled') || urlParams.has('code') || urlParams.has('stripe_success') || urlParams.has('stripe_refresh')) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleGoogleAuthCallback = async (code: string) => {
    try {
      const { error } = await supabase.functions.invoke('google-calendar-oauth', {
        body: {
          action: 'exchangeCode',
          code,
          redirectUri: `${window.location.origin}/dashboard/business-settings`,
        }
      });

      if (error) throw error;
      toast.success("Google Calendar connected successfully!");
      refetchCalendar();
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      toast.error("Failed to connect Google Calendar");
    }
  };

  const handleSetupPayouts = async () => {
    setIsConnectingStripe(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Not authenticated. Please refresh the page and try again.');
      }

      const { data: functionData, error: functionError } = await supabase.functions.invoke('stripe-connect', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          action: 'createConnectAccount',
          refresh_url: `${window.location.origin}/dashboard/business-settings?stripe_refresh=true`,
          return_url: `${window.location.origin}/dashboard/business-settings?stripe_success=true`,
        },
      });

      if (functionError) throw functionError;

      if (functionData?.alreadyConnected) {
        toast.success("You're already set up to get paid!");
        await refetchPayout();
        return;
      }

      if (functionData?.url) {
        window.location.href = functionData.url;
      } else {
        throw new Error('Failed to start payout setup');
      }
    } catch (error: any) {
      console.error("Failed to set up payouts:", error);
      toast.error('Failed to start payout setup. Please try again.');
    } finally {
      setIsConnectingStripe(false);
    }
  };

  const handleManagePayoutAccount = async () => {
    setIsOpeningPayoutAccount(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { action: 'createLoginLink' },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('Failed to open payout account');
      }
    } catch (error: any) {
      console.error("Failed to open payout account:", error);
      toast.error('Failed to open payout account. Please try again.');
    } finally {
      setIsOpeningPayoutAccount(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Business Settings</h1>
          <p className="text-gray-600">Manage your subscription and integrations</p>
        </div>

        <div className="grid gap-6 max-w-4xl">
          {/* Subscription Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Subscription Plan
              </CardTitle>
              <CardDescription>
                Manage your billing and subscription preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg">
                      {subscription.plan === "free" ? "Free Plan" : "Pro Plan"}
                    </h3>
                    <Badge variant={subscription.plan === "free" ? "secondary" : "default"}>
                      {subscription.plan === "free" ? "Current" : "Active"}
                    </Badge>
                  </div>
                  <p className="text-gray-600">
                    {subscription.plan === "free"
                      ? "$0/month + 3.0% Figgy fee per online payment, plus payment processing fees"
                      : "$19/month + 0.75% Figgy fee per online payment, plus payment processing fees"
                    }
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">
                    {subscription.plan === "free" ? "$0" : "$19"}
                  </div>
                  <div className="text-sm text-gray-500">
                    {subscription.plan === "free" ? "per month" : "per month"}
                  </div>
                </div>
              </div>

              {subscription.plan === "free" && (
                <div className="border rounded-lg p-4 bg-gradient-to-r from-orange-50 to-yellow-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        Upgrade to Pro
                      </h4>
                      <ul className="text-sm text-orange-800 space-y-1 mb-4">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Unlimited bookings
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Lower per-payment fees (0.75% vs 3.0%)
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Google Calendar sync
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Priority customer support
                        </li>
                      </ul>
                      <Button
                        onClick={() => {
                          console.log('[BusinessSettings] Upgrade to Pro clicked');
                          upgradeToPro();
                        }}
                        disabled={isUpgrading}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        {isUpgrading ? (
                          "Starting checkout..."
                        ) : (
                          <>
                            Upgrade to Pro
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                      {upgradeError && (
                        <p className="text-sm text-red-600 mt-2">{upgradeError}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {subscription.plan === "pro" && (
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">You're on the Pro plan</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={getStripePortalUrl}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Manage Billing
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payout Setup */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Payouts
              </CardTitle>
              <CardDescription>
                Set up and manage how you receive client payments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingPayout ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-gray-300 animate-pulse" />
                    <p className="text-sm text-gray-600">Loading payout status...</p>
                  </div>
                </div>
              ) : payoutStatus === 'payments_enabled' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Payouts Active</p>
                        <p className="text-sm text-green-600">
                          You're ready to accept online payments. Payouts are deposited to your bank account on a rolling 7-day schedule, less applicable fees.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleManagePayoutAccount}
                    disabled={isOpeningPayoutAccount}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {isOpeningPayoutAccount ? 'Opening...' : 'Manage payout account'}
                  </Button>
                </div>
              ) : payoutStatus === 'pending_verification' ? (
                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600 shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800">Verification in progress</p>
                      <p className="text-sm text-blue-600">
                        Your information is being reviewed. We'll update your payout status when verification is complete.
                      </p>
                    </div>
                  </div>
                </div>
              ) : payoutStatus === 'requirements_due' ? (
                <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-amber-400 shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Setup Incomplete</p>
                      <p className="text-sm text-amber-700">
                        Complete a few secure details to start accepting online payments.
                      </p>
                    </div>
                  </div>
                  <Button onClick={handleSetupPayouts} disabled={isConnectingStripe} variant="outline" className="shrink-0 ml-4">
                    {isConnectingStripe ? "Opening..." : "Finish payout setup"}
                  </Button>
                </div>
              ) : payoutStatus === 'restricted' ? (
                <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                    <div>
                      <p className="font-medium text-red-800">Payout Issue</p>
                      <p className="text-sm text-red-600">
                        There's an issue with your payout account that needs to be resolved.
                      </p>
                    </div>
                  </div>
                  <Button onClick={handleSetupPayouts} disabled={isConnectingStripe} variant="destructive" className="shrink-0 ml-4">
                    {isConnectingStripe ? "Opening..." : "Resolve payout issue"}
                  </Button>
                </div>
              ) : (
                /* not_started */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-gray-300" />
                      <div>
                        <p className="font-medium">Not Set Up</p>
                        <p className="text-sm text-gray-600">
                          Set up payouts to accept online payments from clients
                        </p>
                      </div>
                    </div>
                    <Button onClick={handleSetupPayouts} disabled={isConnectingStripe}>
                      {isConnectingStripe ? "Opening..." : "Set up payouts"}
                    </Button>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400">
                Secure payments and payouts are powered by Stripe.
              </p>
            </CardContent>
          </Card>

          {/* Google Calendar Integration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Google Calendar Integration
              </CardTitle>
              <CardDescription>
                Sync your bookings with Google Calendar and check availability
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {subscription.plan === "free" ? (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="p-2 bg-amber-100 rounded-full shrink-0">
                    <Lock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Pro plan required</p>
                    <p className="text-sm text-gray-600">Upgrade to sync bookings with Google Calendar.</p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end">
                    <Button
                      size="sm"
                      className="bg-figgy-500 hover:bg-figgy-600"
                      onClick={() => {
                        console.log('[BusinessSettings] Calendar upgrade clicked');
                        upgradeToPro();
                      }}
                      disabled={isUpgrading}
                    >
                      <Zap className="mr-1 h-3 w-3" />
                      {isUpgrading ? "Starting checkout..." : "Upgrade"}
                    </Button>
                    {upgradeError && (
                      <p className="text-xs text-red-600 mt-1">{upgradeError}</p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${integration.isConnected ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <div>
                        <p className="font-medium">
                          {integration.isConnected ? "Connected" : "Not Connected"}
                        </p>
                        <p className="text-sm text-gray-600">
                          {integration.isConnected
                            ? "Your bookings are syncing with Google Calendar"
                            : "Connect to sync bookings and check availability"
                          }
                        </p>
                      </div>
                    </div>
                    {integration.isConnected ? (
                      <Button variant="outline" onClick={disconnectGoogleCalendar}>
                        Disconnect
                      </Button>
                    ) : (
                      <Button onClick={connectGoogleCalendar} disabled={isConnecting}>
                        {isConnecting ? "Connecting..." : "Connect"}
                      </Button>
                    )}
                  </div>

                  {integration.isConnected && (
                    <div className="space-y-3">
                      <h4 className="font-medium">Calendar Sync Features:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Automatic booking creation
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Booking cancellation sync
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Availability conflict detection
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Real-time schedule updates
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Calendar Subscription (ICS Feed) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rss className="w-5 h-5" />
                Calendar Subscription
              </CardTitle>
              <CardDescription>
                Subscribe to your bookings in Apple Calendar, Outlook, or any calendar app — no sign-in required
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {icsLoading ? (
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <div className="w-3 h-3 rounded-full bg-gray-200 animate-pulse" />
                  Generating subscription link...
                </div>
              ) : feedUrl ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 border rounded-lg">
                    <input
                      readOnly
                      value={feedUrl}
                      className="flex-1 bg-transparent text-sm text-gray-700 outline-none min-w-0 font-mono"
                      onFocus={e => e.target.select()}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(feedUrl);
                          toast.success('Subscription link copied to clipboard!');
                        } catch {
                          toast.error('Could not copy — select and copy the link manually.');
                        }
                      }}
                    >
                      <Copy className="w-3.5 h-3.5 mr-1.5" />
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Paste this URL into your calendar app to subscribe. Bookings update automatically every hour.
                  </p>
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-gray-500 h-auto py-1"
                      onClick={async () => {
                        try {
                          await rotateIcal();
                          toast.success('Subscription link rotated. Update any existing subscriptions with the new URL.');
                        } catch {
                          toast.error('Failed to rotate subscription link. Please try again.');
                        }
                      }}
                    >
                      <RefreshCw className="w-3 h-3 mr-1.5" />
                      Rotate link
                    </Button>
                    <span className="text-xs text-gray-400">Rotating invalidates existing subscriptions</span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  Subscription link unavailable — ensure your profile is fully set up.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Additional Settings
              </CardTitle>
              <CardDescription>
                More business configuration options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600">
                Additional business settings and integrations will be available here.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BusinessSettingsPage;
